// src/components/DeviceUpdatesPage.js

import React, { useState, useEffect } from "react";
import {
  Typography,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Box,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
} from "@mui/material";
import { db } from "../firebase";
import { collection, query, orderBy, limit, getDocs } from "firebase/firestore";
import { PageHeader } from "./ui";

// dailyDeviceUpdates grows every day; only the latest records are loaded.
const MAX_UPDATES = 1000;

function DeviceUpdatesPage() {
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const fetchDeviceUpdates = async () => {
      setLoading(true);
      setError(null);
      try {
        const updatesCollectionRef = collection(db, "dailyDeviceUpdates");
        const q = query(
          updatesCollectionRef,
          orderBy("timestamp", "desc"),
          limit(MAX_UPDATES),
        );

        const querySnapshot = await getDocs(q);
        const updatesData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setUpdates(updatesData);
      } catch (err) {
        console.error("Error fetching device updates:", err);
        setError(
          "Failed to fetch device updates. Please ensure the collection 'dailyDeviceUpdates' exists and has a 'timestamp' field for ordering. Also check Firestore permissions.",
        );
      }
      setLoading(false);
    };

    fetchDeviceUpdates();
  }, []);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "N/A";
    if (timestamp.toDate) {
      return timestamp.toDate().toLocaleString();
    }
    return new Date(timestamp).toLocaleString();
  };

  // Filtered updates based on search term
  const filteredUpdates = updates.filter((update) => {
    const term = searchTerm.toLowerCase();
    return (
      (update.machineId && update.machineId.toLowerCase().includes(term)) ||
      (update.licenseKey && update.licenseKey.toLowerCase().includes(term)) ||
      (update.firmwareVersion &&
        update.firmwareVersion.toLowerCase().includes(term))
    );
  });

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" sx={{ p: 2 }}>
        Error: {error}
      </Typography>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <PageHeader
        eyebrow="System"
        title="Device updates"
        subtitle={`Daily check-ins reported by devices (latest ${MAX_UPDATES.toLocaleString()}).`}
      />

      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          label="Search by Machine ID, License Key, or Firmware"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size={isMobile ? "small" : "medium"}
        />
      </Box>

      {updates.length === 0 && !loading ? (
        <Typography sx={{ mt: 2 }}>No device updates found.</Typography>
      ) : filteredUpdates.length === 0 && !loading ? (
        <Typography sx={{ mt: 2 }}>
          No updates match your search criteria.
        </Typography>
      ) : (
        <>
          {isMobile ? (
            // Mobile Card View
            <Box sx={{ display: "grid", gap: 1.5 }}>
              {filteredUpdates.map((update) => (
                <Box key={update.id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" color="textSecondary">
                        Machine ID
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {update.machineId || "N/A"}
                      </Typography>

                      <Typography variant="subtitle2" color="textSecondary">
                        License Key
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {update.licenseKey || "N/A"}
                      </Typography>

                      <Typography variant="subtitle2" color="textSecondary">
                        Firmware Version
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {update.firmwareVersion || "N/A"}
                      </Typography>

                      <Typography variant="subtitle2" color="textSecondary">
                        Timestamp
                      </Typography>
                      <Typography variant="body2">
                        {formatTimestamp(update.timestamp)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>
              ))}
            </Box>
          ) : (
            // Desktop Table View
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ mt: 2, overflowX: "auto" }}
            >
              <Table sx={{ minWidth: 650 }} aria-label="device updates table">
                <TableHead>
                  <TableRow>
                    <TableCell>Machine ID</TableCell>
                    <TableCell>License Key</TableCell>
                    <TableCell>Firmware Version</TableCell>
                    <TableCell>Timestamp</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredUpdates.map((update) => (
                    <TableRow
                      key={update.id}
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    >
                      <TableCell component="th" scope="row">
                        {update.machineId || "N/A"}
                      </TableCell>
                      <TableCell>{update.licenseKey || "N/A"}</TableCell>
                      <TableCell>{update.firmwareVersion || "N/A"}</TableCell>
                      <TableCell>{formatTimestamp(update.timestamp)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Box>
  );
}

export default DeviceUpdatesPage;
